import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  effect,
  inject,
  input,
  DOCUMENT,
} from '@angular/core';

import {
  Config,
  Data,
  Layout,
  PlotlyHTMLElement,
  newPlot,
  react,
  purge,
  Plots
} from 'plotly.js-basic-dist-min';

@Component({
  selector: 'app-plotly-plot-lokal',
  template: `
    <div #plot class="plot-container"></div>
  `,
  styles: [`
    .plot-container {
      width: 100%;
      height: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlotlyPlotLokalComponent implements AfterViewInit {

  readonly id = input.required<string>();
  readonly data = input.required<Data[]>();
  readonly layout = input.required<Partial<Layout>>();
  readonly config = input.required<Partial<Config>>();

  @ViewChild('plot', { static: true })
  plotDiv!: ElementRef<HTMLDivElement>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly window = inject(DOCUMENT).defaultView;

  private plot?: PlotlyHTMLElement;
  private resizeObserver?: ResizeObserver;
  private initialized = false;

  constructor() {

    effect(() => {

      if (!this.initialized || !this.plot) {
        return;
      }

      react(
        this.plot,
        this.data(),
        this.layout(),
        this.config()
      );

    });
  }

  async ngAfterViewInit(): Promise<void> {

    const div = this.plotDiv.nativeElement;

    this.plot = await newPlot(
      div,
      this.data(),
      this.layout(),
      this.config()
    );

    this.initialized = true;

    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (this.plot) {
          Plots.resize(this.plot);
        }
      });
    });

    if (div.parentElement) {
      this.resizeObserver.observe(div.parentElement);
    }

    this.window?.addEventListener(
      'resize',
      this.onResize,
      { passive: true }
    );

    this.destroyRef.onDestroy(() => {

      this.window?.removeEventListener(
        'resize',
        this.onResize
      );

      this.resizeObserver?.disconnect();

      if (this.plot) {
        purge(this.plot);
      }
    });
  }

  clear(): void {

    if (!this.plot) {
      return;
    }

    react(
      this.plot,
      [],
      {},
      this.config()
    );
  }

  resizePlot(): void {

    if (this.plot) {
      Plots.resize(this.plot);
    }
  }

  private readonly onResize = () => {

    if (!this.plot) {
      return;
    }

    requestAnimationFrame(() => {
      Plots.resize(this.plot!);
    });
  };
}
/*
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  ViewChild,
  DOCUMENT,
  input,
} from '@angular/core';

import { Config, Data, Layout, PlotlyHTMLElement, newPlot, react, Plots } from 'plotly.js-basic-dist-min';

@Component({
  selector: 'app-plotly-plot-lokal',
  template: `
    <div #plot style="width:100%; height:100%;"></div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlotlyPlotLokalComponent implements AfterViewInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private window = inject(DOCUMENT).defaultView;

  readonly id = input.required<string>();
  readonly data = input.required<Data[]>();
  readonly layout = input.required<Partial<Layout>>();
  readonly config = input.required<Partial<Config>>();

  @ViewChild('plot', { static: true }) plotDiv!: ElementRef<HTMLDivElement>;

  private resizeObserver!: ResizeObserver;
  private plotInstance!: PlotlyHTMLElement;

  ngAfterViewInit(): void {
    const div = this.plotDiv.nativeElement;

    newPlot(div, this.data(), this.layout(), this.config()).then(plot => {
      this.plotInstance = plot;

      // Window resize
      this.window?.addEventListener('resize', this.onWindowResize);

      // Resize observer for parent/container
      this.resizeObserver = new ResizeObserver(() => Plots.resize(div));
      if (div.parentElement) this.resizeObserver.observe(div.parentElement);
    });
  }

  // React to input changes (data or layout)
  ngOnChanges(): void {
    if (this.plotDiv && this.plotInstance) {
      react(this.plotDiv.nativeElement, this.data(), this.layout(), this.config());
    }
  }

  public resizePlot(): void {
    if (this.plotDiv) Plots.resize(this.plotDiv.nativeElement);
  }

  private onWindowResize = () => {
    if (this.plotDiv) Plots.resize(this.plotDiv.nativeElement);
  };

  ngOnDestroy(): void {
    this.window?.removeEventListener('resize', this.onWindowResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.plotInstance) (this.plotInstance as any).remove();
  }
}
*/
